"use client";

import * as PopoverPrimitives from "@radix-ui/react-popover";

import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { FaRegCircleXmark } from "react-icons/fa6";
import { RiCheckLine, RiCloseLine, RiSearchLine } from "react-icons/ri";

import Input from "@/components/ui/input";

import { formatPhoneNumber } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";

/**
 * Results driven by the caller instead of `onSearch`, for pickers whose search
 * is a paged query (e.g. `useInfiniteQuery`). The select reports the typed term
 * and renders `results`; the caller owns fetching, debouncing and paging, the
 * same split as the platform's other infinite pickers (`LoadTemplateMenu`).
 */
export interface APISearchSelectRemoteResults<T> {
  results: T[];
  /** Fires with the raw typed term on every change; the caller debounces. */
  onSearchTermChange: (term: string) => void;
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /** Called when the end-of-list sentinel scrolls into view. */
  onLoadMore?: () => void;
}

// Context for sharing state between components
type APISearchSelectContextType<T> = {
  open: boolean;
  setOpen: (open: boolean) => void;
  value?: T;
  values?: T[]; // For multi-select mode
  onValueChange: (value?: T) => void;
  onValuesChange?: (values: T[]) => void; // For multi-select mode
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  options: T[];
  setOptions: (options: T[]) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  /** Row id (`data-row-id`) of the highlighted row, or null. Rows are found in
   *  the DOM, so caller-composed rows navigate the same as generated ones. */
  highlightedId: string | null;
  setHighlightedId: React.Dispatch<React.SetStateAction<string | null>>;
  /** The scrolling list inside the popover; the input's arrow keys read rows
   *  from it. Set by `APISearchSelectContent`. */
  listNode: HTMLDivElement | null;
  /** id of the list, so the input can point at it and its rows for screen readers */
  listboxId: string;
  setListNode: (node: HTMLDivElement | null) => void;
  /** The caller owns the search text, so the select never resets it itself. */
  isSearchTermControlled: boolean;
  formatOption: (
    option: T,
    values?: T[],
  ) => {
    id: string;
    name: string;
    optionNode?: React.ReactNode;
    containerClassName?: string;
  };
  onSearch?: (value: string) => Promise<T[] | null>;
  remote?: APISearchSelectRemoteResults<T>;
  clearAfterSelect: boolean;
  isFormatNumber: boolean;
  isUserInputRef: React.MutableRefObject<boolean>;
  isMulti: boolean;
};

const ROW_SELECTOR = '[data-api-search-select-row]:not([aria-disabled="true"])';

const APISearchSelectContext = createContext<
  APISearchSelectContextType<any> | undefined
>(undefined);

const useAPISearchSelect = <T,>() => {
  const context = useContext(APISearchSelectContext);
  if (context === undefined) {
    throw new Error(
      "APISearchSelect components must be used within an APISearchSelect",
    );
  }
  return context as APISearchSelectContextType<T>;
};

// Root component
export interface APISearchSelectProps<T> {
  value?: T;
  values?: T[]; // Multi-select values
  defaultValue?: T;
  onValueChange?: (value?: T) => void;
  onValuesChange?: (values: T[]) => void;
  /** Fetch options for a typed term. Not needed when `remote` is passed. */
  onSearch?: (value: string) => Promise<T[] | null>;
  /** Caller-driven (e.g. paged) results; replaces `onSearch` when present. */
  remote?: APISearchSelectRemoteResults<T>;
  formatOption: (
    option: T,
    values?: T[],
  ) => {
    id: string;
    name: string;
    optionNode?: React.ReactNode;
    containerClassName?: string;
  };
  clearAfterSelect?: boolean;
  isFormatNumber?: boolean;
  isMulti?: boolean;
  /** Controls the typed text. Pass with `onSearchTermChange` to own it, e.g. to
   *  keep it after a pick or save it as a free-text value. */
  searchTerm?: string;
  onSearchTermChange?: (term: string) => void;
  children: React.ReactNode;
}

const APISearchSelect = <T,>({
  value,
  values,
  defaultValue,
  onValueChange,
  onValuesChange,
  onSearch,
  formatOption,
  clearAfterSelect = false,
  isFormatNumber = false,
  isMulti = false,
  remote,
  searchTerm: controlledSearchTerm,
  onSearchTermChange,
  children,
}: APISearchSelectProps<T>) => {
  const [open, setOpen] = useState(false);
  const [uncontrolledSearchTerm, setUncontrolledSearchTerm] = useState("");
  const isSearchTermControlled = controlledSearchTerm !== undefined;
  const searchTerm = controlledSearchTerm ?? uncontrolledSearchTerm;
  const setSearchTerm = (term: string) => {
    if (!isSearchTermControlled) setUncontrolledSearchTerm(term);
    onSearchTermChange?.(term);
  };
  const [options, setOptions] = useState<T[]>([]);
  const [selectedValue, setSelectedValue] = useState<T | undefined>(value);
  const [selectedValues, setSelectedValues] = useState<T[]>(values || []);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [listNode, setListNode] = useState<HTMLDivElement | null>(null);
  const listboxId = useId();
  const prevSearchTermRef = useRef<string>("");
  const valueRef = useRef<T | undefined>(value);
  const previousValueRef = useRef<T | undefined>(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const isUserInputRef = useRef<boolean>(false);

  // Update the ref when value changes
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    const previousValue = previousValueRef.current;
    previousValueRef.current = value;
    if (value !== undefined) {
      const formattedOption = formatOption(value, values);
      setSelectedValue(value);
      isUserInputRef.current = false;
      setSearchTerm(formattedOption.name);
      prevSearchTermRef.current = formattedOption.name;
      return;
    }
    if (previousValue === undefined) return;
    // The parent cleared its value. Drop the selection only while it is still
    // that value, so a pick made since survives a parent still loading it.
    const previousId = formatOption(previousValue, values).id;
    setSelectedValue((current) =>
      current !== undefined && formatOption(current, values).id === previousId
        ? undefined
        : current,
    );
  }, [value, formatOption, values]);

  useEffect(() => {
    if (isMulti) {
      setSelectedValues(values || []);
    }
  }, [values, isMulti]);

  useEffect(() => {
    if (defaultValue && value === undefined && !selectedValue) {
      const formattedOption = formatOption(defaultValue, values);
      setSelectedValue(defaultValue);
      isUserInputRef.current = false;
      setSearchTerm(formattedOption.name);
      prevSearchTermRef.current = formattedOption.name;
    } else if (
      value === undefined &&
      defaultValue === undefined &&
      !selectedValue &&
      searchTerm !== "" &&
      searchTerm === prevSearchTermRef.current
    ) {
      setSelectedValue(undefined);
      isUserInputRef.current = false;
      setSearchTerm("");
      prevSearchTermRef.current = "";
    }
  }, [defaultValue, formatOption, value, searchTerm, selectedValue, values]);

  const handleValueChange = (newValue?: T) => {
    if (isMulti) {
      if (!newValue) return;
      const formatted = formatOption(newValue, values);
      const exists = selectedValues.some(
        (v) => formatOption(v).id === formatted.id,
      );
      const next = exists
        ? selectedValues.filter((v) => formatOption(v).id !== formatted.id)
        : [...selectedValues, newValue];
      setSelectedValues(next);
      onValuesChange?.(next);
      setOpen(true); // keep open for more selections
      isUserInputRef.current = false;
      setSearchTerm(""); // clear input after add
      // The remote list must follow the cleared box, or it keeps showing the
      // last term's results under an empty input.
      remote?.onSearchTermChange("");
      return;
    }
    if (newValue === selectedValue) {
      setSelectedValue(undefined);
      onValueChange?.(undefined);
    } else {
      const formattedOption = newValue ? formatOption(newValue, values) : null;
      prevSearchTermRef.current = formattedOption?.name || "";
      setSelectedValue(newValue);
      isUserInputRef.current = false;
      setSearchTerm(clearAfterSelect ? "" : formattedOption?.name || "");
      setOpen(false);
      onValueChange?.(newValue);
      if (clearAfterSelect && newValue) {
        setSelectedValue(undefined);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        !document
          .querySelector("[data-radix-popper-content-wrapper]")
          ?.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [open, setOpen]);

  return (
    <APISearchSelectContext.Provider
      value={{
        open,
        setOpen,
        value: selectedValue,
        values: selectedValues,
        onValueChange: handleValueChange,
        onValuesChange: (vals: T[]) => {
          setSelectedValues(vals);
          onValuesChange?.(vals);
        },
        searchTerm,
        setSearchTerm,
        // In remote mode the caller's results and loading state are the source
        // of truth; the local copies stay unused.
        options: remote ? remote.results : options,
        setOptions,
        isLoading: remote ? !!remote.isLoading : isLoading,
        setIsLoading,
        highlightedId,
        setHighlightedId,
        listNode,
        setListNode,
        listboxId,
        isSearchTermControlled,
        formatOption,
        onSearch,
        remote,
        clearAfterSelect,
        isFormatNumber,
        isUserInputRef,
        isMulti,
      }}
    >
      <PopoverPrimitives.Root
        open={open}
        onOpenChange={(newOpen) => {
          setOpen(newOpen);
          // On close, snap the input back to the committed selection. If nothing
          // was picked, clear the typed query so an un-selected free-text value
          // can't linger in the field and look accepted — this is a pick-only
          // selector, so text that matches no option is never a value. A caller
          // that owns the text decides what typed text means.
          if (!newOpen && !isSearchTermControlled) {
            isUserInputRef.current = false;
            setSearchTerm(
              selectedValue ? formatOption(selectedValue, values).name : "",
            );
          }
        }}
      >
        {children}
      </PopoverPrimitives.Root>
    </APISearchSelectContext.Provider>
  );
};

APISearchSelect.displayName = "APISearchSelect";

// Anchor component: positions the list. Put the input, and anything that
// should sit with it (e.g. chips), inside.
const APISearchSelectAnchor = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, forwardedRef) => (
  <PopoverPrimitives.Anchor asChild>
    <div
      ref={forwardedRef}
      data-api-search-select-anchor=""
      className={cx("relative w-full", className)}
      {...props}
    />
  </PopoverPrimitives.Anchor>
));

APISearchSelectAnchor.displayName = "APISearchSelectAnchor";

// Input component: the search box. Extra input props pass through; the
// caller's onKeyDown runs for keys the list didn't use.
type APISearchSelectInputProps = Omit<
  React.ComponentPropsWithoutRef<typeof Input>,
  "value" | "onChange" | "icon" | "type"
> & {
  hideSearchIcon?: boolean;
  openOnFocus?: boolean;
  /** Fires on every keystroke, before the debounced search. */
  onInputChange?: (value: string) => void;
};

const APISearchSelectInput = React.forwardRef<
  HTMLInputElement,
  APISearchSelectInputProps
>(
  (
    {
      className,
      hideSearchIcon = false,
      openOnFocus = false,
      onFocus,
      onInputChange,
      onKeyDown,
      ...props
    },
    forwardedRef,
  ) => {
    const {
      open,
      setOpen,
      searchTerm,
      setSearchTerm,
      options,
      setOptions,
      value,
      values,
      onValueChange,
      highlightedId,
      setHighlightedId,
      listNode,
      listboxId,
      isSearchTermControlled,
      formatOption,
      onSearch,
      remote,
      isFormatNumber,
      setIsLoading,
      isUserInputRef,
    } = useAPISearchSelect();
    const onSearchTermChange = remote?.onSearchTermChange;

    const prevSearchTermRef = useRef<string>("");
    const valueRef = useRef(value);
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(forwardedRef, () => inputRef.current!);
    const currentSearchTermRef = useRef<string>("");

    useEffect(() => {
      valueRef.current = value;
    }, [value]);

    // A new list resets the highlight, except when the list only grew: in remote
    // mode the next page arrives as a fresh array (with fresh objects), and
    // resetting there would drop a user's place mid-scroll. Compare by option id.
    // formatOption is usually an inline function; read it through a ref so the
    // effect still runs only when `options` changes.
    const formatOptionRef = useRef(formatOption);
    formatOptionRef.current = formatOption;
    const prevOptionIdsRef = useRef<string[]>([]);
    useEffect(() => {
      const ids = options.map((option) => formatOptionRef.current(option).id);
      const prevIds = prevOptionIdsRef.current;
      prevOptionIdsRef.current = ids;
      const appended =
        prevIds.length > 0 &&
        ids.length > prevIds.length &&
        prevIds.every((id, i) => ids[i] === id);
      if (appended) return;
      setHighlightedId(null);
    }, [options, setHighlightedId]);

    const formattedSearchValue = React.useMemo(() => {
      return isFormatNumber ? formatPhoneNumber(searchTerm, false) : searchTerm;
    }, [searchTerm, isFormatNumber]);

    useEffect(() => {
      if (
        searchTerm &&
        searchTerm.length > 0 &&
        searchTerm !== prevSearchTermRef.current &&
        isUserInputRef.current
      ) {
        const searchTermToUse = searchTerm;
        currentSearchTermRef.current = searchTermToUse;

        // Remote mode: hand the term to the caller, whose query debounces.
        if (onSearchTermChange) {
          onSearchTermChange(searchTermToUse);
          prevSearchTermRef.current = searchTermToUse;
          return;
        }
        if (!onSearch) return;

        const delayDebounceFn = setTimeout(async () => {
          setIsLoading(true);
          const results = await onSearch(searchTermToUse);

          if (results === null) {
            // Handle error case - could show toast here if needed
            setIsLoading(false);
          } else {
            // Only update if this search term is still the current one
            if (currentSearchTermRef.current === searchTermToUse) {
              setOptions(results);
              prevSearchTermRef.current = searchTermToUse;
              setIsLoading(false);
            } else {
              setIsLoading(false);
            }
          }
        }, 300);

        return () => {
          clearTimeout(delayDebounceFn);
        };
      } else if (
        searchTerm.length === 0 &&
        prevSearchTermRef.current !== "" &&
        !valueRef.current &&
        isUserInputRef.current
      ) {
        if (onSearchTermChange) {
          onSearchTermChange("");
        } else if (onSearch) {
          const handleEmptySearch = async () => {
            const results = await onSearch("");
            if (results !== null) {
              setOptions([]);
            }
          };
          handleEmptySearch();
        }

        if (!valueRef.current) {
          onValueChange(undefined);
          prevSearchTermRef.current = "";
        }
      }
    }, [searchTerm, onSearch, onSearchTermChange, onValueChange]);

    const rows = () =>
      Array.from(listNode?.querySelectorAll<HTMLElement>(ROW_SELECTOR) ?? []);

    const moveHighlight = (step: 1 | -1) => {
      const list = rows();
      if (list.length === 0) return;
      const current = list.findIndex(
        (row) => row.dataset.rowId === highlightedId,
      );
      const next =
        current === -1
          ? step === 1
            ? 0
            : list.length - 1
          : (current + step + list.length) % list.length;
      setHighlightedId(list[next].dataset.rowId ?? null);
      list[next].scrollIntoView({ block: "nearest" });
    };

    /** Returns true when the list used the key, so the caller's handler skips it. */
    const handleListKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!open) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          setOpen(true);
          e.preventDefault();
          return true;
        }
        return false;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          moveHighlight(1);
          return true;
        case "ArrowUp":
          e.preventDefault();
          moveHighlight(-1);
          return true;
        case "Enter": {
          e.preventDefault();
          const row = rows().find(
            (candidate) => candidate.dataset.rowId === highlightedId,
          );
          if (!row) return false;
          row.click();
          return true;
        }
        case "Escape":
          e.preventDefault();
          setOpen(false);
          // A caller that owns the text also hears Escape, to reset its own state
          if (isSearchTermControlled) return false;
          if (value) {
            const formattedOption = formatOption(value, values);
            isUserInputRef.current = false;
            setSearchTerm(formattedOption.name);
          } else if (valueRef.current) {
            const formattedOption = formatOption(valueRef.current, values);
            isUserInputRef.current = false;
            setSearchTerm(formattedOption.name);
          }
          return true;
        default:
          return false;
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (handleListKey(e)) return;
      onKeyDown?.(e);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      isUserInputRef.current = true;
      // A new query brings new rows; start the highlight over
      setHighlightedId(null);
      setSearchTerm(newValue);
      onInputChange?.(newValue);

      if (newValue.length > 0) {
        setOpen(true);
      } else {
        // Always clear the selected value when input is cleared
        onValueChange(undefined);
      }
    };

    const handleFocus = async (e: React.FocusEvent<HTMLInputElement>) => {
      if (openOnFocus && onSearch) {
        // Launch search with empty parameters and open dropdown
        const results = await onSearch("");
        if (results !== null) {
          setOptions(results);
        }
        setOpen(true);
      }
      if (openOnFocus && remote) setOpen(true);

      onFocus?.(e);
    };

    const handleClear = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      isUserInputRef.current = true;
      setSearchTerm("");
      onInputChange?.("");
      onValueChange(undefined);
      inputRef.current?.focus();
    };

    return (
      <>
        <Input
          ref={inputRef}
          type="text"
          icon={hideSearchIcon ? undefined : RiSearchLine}
          value={formattedSearchValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          className={cx("w-full", !!value && "pr-8", className)}
          // Arrow keys move a highlight rather than focus, so point screen
          // readers at the list and its highlighted row
          aria-autocomplete="list"
          aria-controls={listNode ? listboxId : undefined}
          aria-activedescendant={
            listNode && highlightedId ? highlightedId : undefined
          }
          {...props}
        />
        {value ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-muted transition-colors hover:text-foreground-secondary"
            tabIndex={-1}
          >
            <RiCloseLine className="h-4 w-4" />
          </button>
        ) : null}
      </>
    );
  },
);

APISearchSelectInput.displayName = "APISearchSelectInput";

// Badges for the picked values in multi-select mode
const APISearchSelectBadges = () => {
  const { values, onValuesChange, formatOption, isMulti } =
    useAPISearchSelect();
  if (!isMulti || !values || values.length === 0) return null;
  return (
    <div className="mb-1 flex flex-wrap gap-1">
      {values.map((v) => {
        const f = formatOption(v);
        return (
          <span
            key={f.id}
            className={cx(
              "inline-flex items-center rounded bg-muted px-2 py-0.5 text-caption text-foreground-secondary",
              f.containerClassName,
            )}
          >
            <span>{f.optionNode ?? f.name}</span>
            <button
              type="button"
              className="ml-1 text-muted-foreground hover:text-foreground-secondary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const next = values.filter(
                  (vv) => formatOption(vv).id !== f.id,
                );
                onValuesChange?.(next);
              }}
              aria-label="Remove"
            >
              <FaRegCircleXmark />
            </button>
          </span>
        );
      })}
    </div>
  );
};

// Trigger component: the ready-made anchor, badges and input
interface APISearchSelectTriggerProps {
  hasError?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  hideSearchIcon?: boolean;
  openOnFocus?: boolean;
  onFocus?: () => void;
  /** Fires on every keystroke, before the debounced search. */
  onInputChange?: (value: string) => void;
  className?: string;
  ariaLabel?: string;
  dataTestId?: string;
}

const APISearchSelectTrigger = React.forwardRef<
  HTMLDivElement,
  APISearchSelectTriggerProps
>(({ className, ariaLabel, dataTestId, ...inputProps }, forwardedRef) => (
  <APISearchSelectAnchor ref={forwardedRef} className={className}>
    <APISearchSelectBadges />
    <APISearchSelectInput
      {...inputProps}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    />
  </APISearchSelectAnchor>
));

APISearchSelectTrigger.displayName = "APISearchSelectTrigger";

// Content component
interface APISearchSelectContentProps {
  align?: "start" | "center" | "end";
  emptyMessage?: React.ReactNode;
  className?: string;
  /** Classes for the inner scrolling list (e.g. a taller `max-h-*` for
   *  multi-line rows). Pair with the same height on `className`. */
  listClassName?: string;
  dataTestId?: string;
  optionTestIdPrefix?: string;
  /** Controls that remain available even when the search returns no matches. */
  footer?: React.ReactNode;
  /** Rows to show instead of the generated ones: `APISearchSelectItem`s,
   *  `APISearchSelectAction`s, or anything else. Shown while there are any. */
  children?: React.ReactNode;
  /** A row after the generated results (e.g. an `APISearchSelectAction` to
   *  create what was typed). Shown once a search has returned results;
   *  `close` dismisses the list. */
  trailingRow?: (state: {
    optionCount: number;
    close: () => void;
  }) => React.ReactNode;
}

const APISearchSelectContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitives.Content>,
  APISearchSelectContentProps
>(
  (
    {
      align = "start",
      emptyMessage = "No results found",
      className,
      listClassName,
      dataTestId,
      optionTestIdPrefix,
      footer,
      children,
      trailingRow,
      ...props
    },
    forwardedRef,
  ) => {
    const {
      options,
      searchTerm,
      isLoading,
      remote,
      formatOption,
      listNode,
      setListNode,
      listboxId,
      setOpen,
    } = useAPISearchSelect();
    const hasNextPage = !!remote?.hasNextPage;
    const onLoadMore = remote?.onLoadMore;

    // A new result list starts at the top, so the best match isn't left above
    // the fold by the previous list's scroll. A list that only grew (the next
    // page arriving) keeps the user's place.
    const formatOptionRef = useRef(formatOption);
    formatOptionRef.current = formatOption;
    const prevOptionIdsRef = useRef<string[]>([]);
    // Layout effect: reset before paint, so the new list never flashes mid-scroll.
    useLayoutEffect(() => {
      const ids = options.map((option) => formatOptionRef.current(option).id);
      const prevIds = prevOptionIdsRef.current;
      prevOptionIdsRef.current = ids;
      const appended =
        ids.length >= prevIds.length && prevIds.every((id, i) => ids[i] === id);
      if (!appended && listNode) listNode.scrollTop = 0;
    }, [options, listNode]);

    // Infinite scroll sentinel, as in LoadTemplateMenu: the callback ref lives in
    // state because the popover mounts its content after this component renders.
    const [loaderNode, setLoaderNode] = useState<HTMLDivElement | null>(null);
    useEffect(() => {
      if (!loaderNode || !hasNextPage || !onLoadMore) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) onLoadMore();
        },
        { rootMargin: "200px" },
      );
      observer.observe(loaderNode);
      return () => observer.disconnect();
    }, [loaderNode, hasNextPage, onLoadMore]);

    const isComposed = children !== undefined;
    const hasResults = options.length > 0;
    const hasRows = isComposed
      ? React.Children.toArray(children).length > 0
      : hasResults;
    const showContent = !!footer || hasRows || (!isComposed && !!searchTerm);

    if (!showContent) return null;

    const trailingNode =
      !isComposed && hasResults && !isLoading && trailingRow ? (
        <div role="none">
          {trailingRow({
            optionCount: options.length,
            close: () => setOpen(false),
          })}
        </div>
      ) : null;

    const loader = hasNextPage && (
      <div
        ref={setLoaderNode}
        data-testid="api-search-select-load-more"
        className="p-2 text-caption text-muted-foreground"
      >
        {remote?.isFetchingNextPage ? "Loading..." : null}
      </div>
    );

    return (
      <PopoverPrimitives.Portal>
        <PopoverPrimitives.Content
          ref={forwardedRef}
          className={cx(
            "relative z-[60] flex flex-col overflow-hidden rounded-md border border-border shadow-xl shadow-black/[2.5%]",
            "w-[calc(var(--radix-popover-trigger-width))]",
            "max-h-60",
            "bg-background/90 backdrop-blur-sm",
            "text-foreground",
            "text-body md:text-body-lg",
            "will-change-[transform,opacity]",
            "data-[state=closed]:animate-hide",
            "data-[side=bottom]:animate-slide-down-and-fade data-[side=left]:animate-slide-left-and-fade data-[side=right]:animate-slide-right-and-fade data-[side=top]:animate-slide-up-and-fade",
            "data-[side=bottom]:mt-1.5",
            "data-[side=top]:mb-1.5",
            className,
          )}
          sideOffset={0}
          align={align}
          onOpenAutoFocus={(e) => {
            // Prevent the popover from stealing focus from the input
            e.preventDefault();
          }}
          onCloseAutoFocus={(e) => {
            // Prevent focus from being returned to the trigger automatically
            e.preventDefault();
          }}
          onInteractOutside={(e) => {
            // The input lives outside the popover content; clicking or focusing
            // it must not dismiss the list it controls.
            const target = e.target as HTMLElement | null;
            if (target?.closest("[data-api-search-select-anchor]")) {
              e.preventDefault();
            }
          }}
          data-testid={dataTestId}
          {...props}
        >
          <div
            ref={setListNode}
            className={cx(
              "min-h-0 max-h-60 flex-1 overflow-auto overflow-x-hidden p-1",
              listClassName,
            )}
          >
            {/* Only rows go in the listbox: screen readers can skip other
                content there, so status messages sit beside it */}
            <div id={listboxId} role="listbox">
              {isComposed
                ? children
                : options.map((option, index) => (
                    <APISearchSelectItem
                      key={index}
                      value={option}
                      testIdPrefix={optionTestIdPrefix}
                    />
                  ))}
              {trailingNode}
            </div>
            {hasRows ? (
              loader
            ) : searchTerm && isLoading ? (
              <div className="p-2 text-body text-muted-foreground">
                Loading...
              </div>
            ) : searchTerm || footer ? (
              <div className="p-2 text-body text-muted-foreground">
                {emptyMessage}
              </div>
            ) : null}
          </div>
          {footer && (
            <div className="shrink-0 border-t border-border p-2">{footer}</div>
          )}
        </PopoverPrimitives.Content>
      </PopoverPrimitives.Portal>
    );
  },
);

APISearchSelectContent.displayName = "APISearchSelectContent";

// Action component: a row that runs its own `onSelect` instead of picking a
// value, e.g. "Add what I typed". Highlights and takes Enter like any row.
interface APISearchSelectActionProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onSelect"
> {
  onSelect: () => void;
  disabled?: boolean;
  selected?: boolean;
}

const APISearchSelectAction = ({
  onSelect,
  disabled = false,
  selected = false,
  className,
  children,
  onMouseDown,
  onClick,
  onMouseEnter,
  ...props
}: APISearchSelectActionProps) => {
  const { highlightedId, setHighlightedId } = useAPISearchSelect();
  const rowId = useId();

  // A highlighted row that goes away takes the highlight with it, so the
  // input never points screen readers at a row that no longer exists
  useEffect(
    () => () =>
      setHighlightedId((current) => (current === rowId ? null : current)),
    [rowId, setHighlightedId],
  );

  return (
    <div
      {...props}
      role="option"
      aria-selected={selected}
      aria-disabled={disabled || undefined}
      data-api-search-select-row=""
      id={rowId}
      data-row-id={rowId}
      className={cx(
        "flex cursor-pointer items-center rounded px-2 py-1.5 outline-none transition-colors sm:text-body",
        "text-foreground",
        "hover:bg-hover",
        "focus-visible:bg-hover",
        "py-2.5 md:py-1.5",
        selected && "font-semibold",
        highlightedId === rowId && "bg-hover",
        disabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
        className,
      )}
      // Keep focus in the input. Without this, Safari (which doesn't focus a
      // clicked row) blurs the input before the click lands.
      onMouseDown={(e) => {
        onMouseDown?.(e);
        e.preventDefault();
      }}
      onClick={(e) => {
        onClick?.(e);
        if (!disabled) onSelect();
      }}
      onMouseEnter={(e) => {
        onMouseEnter?.(e);
        if (!disabled) setHighlightedId(rowId);
      }}
    >
      {children}
    </div>
  );
};

APISearchSelectAction.displayName = "APISearchSelectAction";

// Item component: a row that picks `value`
interface APISearchSelectItemProps<T> {
  value: T;
  className?: string;
  testIdPrefix?: string;
}

const APISearchSelectItem = <T,>({
  value: option,
  className,
  testIdPrefix,
}: APISearchSelectItemProps<T>) => {
  const { value, values, options, onValueChange, formatOption, isMulti } =
    useAPISearchSelect<T>();

  const formattedOption = formatOption(option, options);
  const isSelected = isMulti
    ? !!values?.some((v) => formatOption(v, values).id === formattedOption.id)
    : !!(value && formatOption(value, options).id === formattedOption.id);

  return (
    <APISearchSelectAction
      onSelect={() => onValueChange(option)}
      selected={isSelected}
      className={className}
      data-testid={
        testIdPrefix ? `${testIdPrefix}-${formattedOption.id}` : undefined
      }
      data-option-id={formattedOption.id}
    >
      <div className="flex w-full items-center justify-between gap-2">
        {formattedOption.optionNode ? (
          <div className={cx("flex-1", formattedOption.containerClassName)}>
            {formattedOption.optionNode}
          </div>
        ) : (
          <span
            className={cx(
              "flex-1 truncate",
              formattedOption.containerClassName,
            )}
          >
            {formattedOption.name}
          </span>
        )}
        {isSelected && (
          <RiCheckLine className="ml-2 h-4 w-4 shrink-0 text-status-success-text" />
        )}
      </div>
    </APISearchSelectAction>
  );
};

APISearchSelectItem.displayName = "APISearchSelectItem";

export {
  APISearchSelect,
  APISearchSelectAction,
  APISearchSelectAnchor,
  APISearchSelectContent,
  APISearchSelectInput,
  APISearchSelectItem,
  APISearchSelectTrigger,
};

// Usage example:
// <APISearchSelect
//   value={selectedUser}
//   onValueChange={setSelectedUser}
//   onSearch={searchUsers}
//   formatOption={(user) => ({ id: user.id, name: user.name })}
// >
//   <APISearchSelectTrigger placeholder="Search users..." />
//   <APISearchSelectContent>
//     {/* Content is automatically populated with APISearchSelectItem components */}
//   </APISearchSelectContent>
// </APISearchSelect>
//
// Composed: own the text and the rows, e.g. to add rows that aren't values.
// <APISearchSelect searchTerm={term} onSearchTermChange={setTerm} formatOption={…}>
//   <APISearchSelectAnchor>
//     {chips}
//     <APISearchSelectInput onBlur={saveTyped} />
//   </APISearchSelectAnchor>
//   <APISearchSelectContent>
//     {results.map((r) => <APISearchSelectAction key={r.id} onSelect={() => add(r)}>{r.name}</APISearchSelectAction>)}
//     {term && <APISearchSelectAction onSelect={() => add(term)}>Add “{term}”</APISearchSelectAction>}
//   </APISearchSelectContent>
// </APISearchSelect>
